/**
 * The Info tab (fix D / R3): everything the rail used to say in its HEADER chips
 * plus the things a player asks mid-game and the old screen never showed —
 * who is who, what the control is, which mode this is, whether the position was
 * custom, and (once it is over) the result and the review link.
 *
 * It replaces `rail/Rail/RailHeader.*`, which spent a permanent 56 px band on
 * three chips (R6.1/R6.2: the rail is one surface and the band was its own
 * card-within-a-card).
 *
 * ONE `useShallow` selector, so the panel renders only when one of ITS fields
 * moves — none of them does per move, and the panel is not even mounted unless
 * the Info tab is on.
 */
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';
import type {Side} from '@/Store/liveGame/state';
import {START_FEN} from '@/Store/liveGame/state';
import {selectGameOver} from '@/Store/liveGame/selectors';

import {openReview} from '../../actions/useGameActions';

export type InfoSeat = {
  name: string;
  rating: number | null;
  color: Side;
  isMe: boolean;
};

type InfoInput = {
  me: string;
  displayName: string;
  myColor: Side | null;
  myRating: number | null;
  opponentName: string;
  opponentRating: number | null;
  gameDetails: string;
  gamePlayMode: string;
  assisted: boolean;
  tournamentMode: 'swiss' | 'arena' | null;
  round: number | null;
  customStart: boolean;
  over: boolean;
  message: string;
  /** the result word; 'Abort' has nothing to review (ResultOverlay's own rule) */
  outcome: string;
  gameUuid: string;
  watching: boolean;
  /** the custom ("parental control") rules this account holds, E5 */
  customRules: string[];
};

export const selectInfo = (s: LiveGameState): InfoInput => ({
  me: s.session.me,
  displayName: s.session.displayName,
  myColor: s.game.myColor,
  myRating: s.game.myRating,
  opponentName: s.game.opponentName,
  opponentRating: s.game.opponentRating,
  gameDetails: s.game.gameDetails,
  gamePlayMode: s.game.gamePlayMode,
  assisted: s.game.assisted,
  tournamentMode: s.tournament.mode,
  round: s.tournament.currentRound,
  // a custom start position is the lobby's FEN option (old `startfen`)
  customStart: !!s.game.startFen && s.game.startFen !== START_FEN,
  over: selectGameOver(s),
  message: s.game.result?.message || '',
  outcome: s.game.result?.outcome || '',
  gameUuid: s.game.result?.gameUuid || '',
  watching: s.watch.active || !!s.watch.replayGameId,
  // E5: the array itself is the handshake's, written once per session, so it
  // cannot move the `useShallow` object per move
  customRules: s.session.customRules,
});

/** "Tournament · Round 3" / "Arena" / "Play & Learn" / "Casual". */
export const modeLabel = (input: InfoInput): string => {
  if (input.watching) {
    return 'Watching';
  }
  if (input.tournamentMode === 'arena' || input.gamePlayMode === 'arena') {
    return 'Arena';
  }
  if (input.tournamentMode === 'swiss' || input.gamePlayMode === 'tournament') {
    return input.round ? `Tournament · Round ${input.round}` : 'Tournament';
  }
  return input.assisted ? 'Play & Learn' : 'Casual';
};

export type InfoPanelModel = {
  /** white first, then black — the two seats of the game on the board */
  seats: InfoSeat[];
  /** "3 + 0 · Rated · Blitz", split on the reducer's own bullet */
  chips: string[];
  mode: string;
  /** the game did not start from the initial position */
  customStart: boolean;
  /** the result line, '' while the game is on */
  result: string;
  gameUuid: string;
  canReview: boolean;
  onReview: () => void;
  /**
   * E5 (rules-gates A9): the active custom rules, for the old `GameRules`
   * badge. Empty for everyone who holds none, which is almost everyone.
   */
  rules: string[];
  /**
   * Fix F-D / D2, copy fix Z1.1: "Play & Learn · Caissa 1200 · a hint after
   * every move" — null outside an assisted game of MY OWN (never while
   * watching someone else's, which would otherwise read this screen's own
   * stale `game.assisted`).
   */
  assistedBanner: string | null;
};

export function useInfoPanelModel(): InfoPanelModel {
  const input = useLiveGame(useShallow(selectInfo));

  const myColor: Side = input.myColor ?? 'white';
  const mine: InfoSeat = {
    name: input.displayName || 'You',
    rating: input.myRating,
    color: myColor,
    isMe: true,
  };
  const theirs: InfoSeat = {
    name: input.opponentName || 'Opponent',
    rating: input.opponentRating,
    color: myColor === 'white' ? 'black' : 'white',
    isMe: false,
  };

  return {
    seats: myColor === 'white' ? [mine, theirs] : [theirs, mine],
    chips: input.gameDetails
      ? input.gameDetails
          .split('•')
          .map(part => part.trim())
          .filter(Boolean)
      : [],
    mode: modeLabel(input),
    customStart: input.customStart,
    result: input.over && input.message ? input.message : '',
    gameUuid: input.gameUuid,
    canReview: !input.watching && !!input.gameUuid && input.outcome !== 'Abort',
    onReview: openReview,
    rules: input.customRules,
    assistedBanner:
      input.assisted && !input.watching
        ? `Play & Learn · ${
            input.opponentName || 'Opponent'
          } · a hint after every move`
        : null,
  };
}

export default useInfoPanelModel;
